import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { api, CoPNewsItem } from '../../../helpers';
import { useApi } from '../../../hooks/useApi';

import Badge from 'landing-page/components/common/Badge';
import { Card } from 'react-bootstrap';

import styles from './CoP.module.scss';

interface CoPProps {
    categories?: CoPCategoryItem[],
    autoFetch?: boolean,
}

interface CoPCategoryItem {
    id: string, // Changed from number to string based on the response
    nama_kategori: string
}

const CopCategoriesPlaceholder: React.FC = () => {
    return (
        <div className='d-flex align-items-center mb-10'>
            <div className='d-flex flex-wrap gap-2'>
                {/* "Semua" badge placeholder */}
                <div className='bg-primary rounded-pill px-6 py-3' style={{ height: '40px', width: '80px' }}>
                    <div className='placeholder-glow'>
                        <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '11px', width: '100%' }}></div>
                    </div>
                </div>
                
                {/* Category badges placeholders */}
                {[1, 2, 3, 4].map((index) => (
                    <div key={index} className='bg-white border rounded-pill px-6 py-3' style={{ height: '40px', width: `${80 + (index * 20)}px` }}>
                        <div className='placeholder-glow'>
                            <div className='placeholder bg-secondary rounded' style={{ height: '11px', width: '100%' }}></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const CopNewsPlaceholder: React.FC = () => {
    return (
        <div className='row'>
            {[1, 2, 3].map((index) => (
                <div key={index} className='col-md-4 mb-4'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden' style={{ height: '420px' }}>
                        <div className='bg-light' style={{ height: '180px' }}>
                            <div className='placeholder-glow h-100 d-flex align-items-center justify-content-center'>
                                <div className='spinner-border text-primary' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </div>
                            </div>
                        </div>
                        <Card.Body className='d-flex flex-column'>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                                <i className='bi bi-tag text-primary'></i>
                                <div className='placeholder-glow'>
                                    <div className='placeholder bg-secondary rounded' style={{ height: '12px', width: '80px' }}></div>
                                </div>
                            </div>
                            <div className='placeholder-glow mb-2'>
                                <div className='placeholder bg-secondary rounded' style={{ height: '24px', width: '100%' }}></div>
                            </div>
                            <div className='placeholder-glow mb-3'>
                                <div className='placeholder bg-secondary rounded' style={{ height: '16px', width: '100%' }}></div>
                                <div className='placeholder bg-secondary rounded mt-1' style={{ height: '16px', width: '80%' }}></div>
                            </div>
                            <div className='mt-auto'>
                                <div className='placeholder-glow'>
                                    <div className='placeholder bg-primary rounded' style={{ height: '20px', width: '150px' }}></div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            ))}
        </div>
    )
}

const CoP: React.FC<CoPProps> = ({
     categories: propCategories = [],
     autoFetch = true
}) => {
    const [copCategories, setCopCategories] = useState<CoPCategoryItem[] | null>(null);
    const [selectedBadge, setSelectedBadge] = useState({id: '', name: 'Semua'}); // default badge
    const [copNews, setCopNews] = useState<CoPNewsItem[]>([]);

    // Updated to use the new COP home endpoint
    const { data, execute: fetchCopHome, loading } = useApi(api.getCOPHome.bind(api));
    
    // New API call for COP news
    const { data: newsData, execute: fetchCopNews, loading: newsLoading } = useApi(api.getCOPNews.bind(api));

    useEffect(() => {
        if (autoFetch && propCategories.length === 0) {
            fetchCopHome();
        } else if (propCategories.length > 0) {
            setCopCategories(propCategories);
        }
    }, [autoFetch, propCategories.length]);

    // Handle the response data when it changes
    useEffect(() => {
        if (data?.data?.kategori_berita) {
            setCopCategories(data.data.kategori_berita);
        }
    }, [data]);

    // Handle news data when it changes
    useEffect(() => {
        if (newsData?.data) {
            setCopNews(newsData.data);
        }
    }, [newsData]);

    // Fetch news when category changes
    useEffect(() => {
        if (selectedBadge.id) {
            fetchCopNews(selectedBadge.id);
        } else {
            // Fetch all news when "Semua" is selected
            fetchCopNews();
        }
    }, [selectedBadge.id]);

    const handleBadgeClick = (id: string, name: string) => {
        setSelectedBadge({id, name});
    };

    return (
        <>
            {loading ? <CopCategoriesPlaceholder /> : (
                <div className='d-flex align-items-center mb-10'>
                    <div className='d-flex flex-wrap'>
                        <Badge
                            variant={selectedBadge.name === 'Semua' ? 'primary' : 'white'}
                            color={selectedBadge.name === 'Semua' ? 'white' : 'gray'}
                            onClick={() => handleBadgeClick('', 'Semua')}
                        >
                            Semua
                        </Badge>
                        {copCategories?.map(item => (
                            <Badge
                                key={item.id}
                                variant={selectedBadge.name === item.nama_kategori ? 'primary' : 'white'}
                                color={selectedBadge.name === item.nama_kategori ? 'white' : 'gray'}
                                onClick={() => handleBadgeClick(item.id, item.nama_kategori)}
                            >
                                {item.nama_kategori}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {newsLoading ? <CopNewsPlaceholder /> : (
                <div className='row'>
                    {copNews.map(item => (
                        <div key={item.id} className='col-md-4 mb-4'>
                            <Link to={`/cop/${item.id}`} className='text-decoration-none'>
                                <Card className={clsx('border-0 shadow-sm rounded-4 overflow-hidden position-relative card-hover', styles.copCard)}>
                                    <Card.Img src={item.upload_thumbnail} style={{ height: '180px', objectFit: 'cover' }} />
                                    <Card.Body className='d-flex flex-column'>
                                        <div className='d-flex align-items-center gap-2 mb-2'>
                                            <i className='bi bi-tag text-primary'></i>
                                            <span className='small text-uppercase text-muted'>{item.kategori.nama_kategori}</span>
                                        </div>
                                        <Card.Title as='h4' className='fw-bold text-dark'>{item.judul}</Card.Title>
                                        <Card.Text as='div' className={clsx('text-muted cardText', styles.cardText)} dangerouslySetInnerHTML={{ __html: item.abstrak }}></Card.Text>
                                        <div className='d-flex mt-auto'>
                                            <span className='text-primary fs-4 fw-bold d-flex align-items-center gap-2 card-link'>
                                                Lihat Selengkapnya
                                                <i className='bi bi-arrow-right fs-4 text-primary arrow-hover'></i>
                                            </span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}

export default CoP;